import type { ModalProps } from '~/util';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { useAppForm } from '~/util/hooks/useAppForm';
import { formOptions } from '@tanstack/react-form';
import { useList } from '~/util/hooks/useList';
import { newLevelMutation } from '~/util/mutations/staff/levels';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { levelPlaceFormInput, levelPlaceFormSchema } from '~/util/forms/level';
import { ListEditPreview } from './-ListEditPreview';
import { levelStatuses } from '~/util/types/levels';

import { levelsQuery } from '~/util/queries/public/levels';
import { useHydratedQuery } from '~/util/hooks/useHydratedQuery';

export const NewLevelSheet: React.FC<ModalProps> = (modalProps) => {
    const list = useList();
    const { data: levels = [] } = useHydratedQuery(levelsQuery({ list }));

    const mutation = useApiMutation({
        ...newLevelMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'add-level-staff',
            defaultValues: {
                list,
                level: { legacy: false, two_player: false },
                creators: [],
                verification: {
                    mobile: false,
                },
            } as levelPlaceFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: levelPlaceFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate(value);
            },
        }),
    );

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:maxs-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Add level</SheetTitle>
                        <SheetDescription>
                            This will add a new level to the list. Note that the
                            verification will be created separately as a new
                            submission for the level after it has been added.
                        </SheetDescription>
                    </SheetHeader>
                    <form
                        id="add-level-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <h3 className="text-xl font-semibold ml-4 mt-4">
                            Level
                        </h3>
                        <p className="text-sm text-secondary-light ml-4 mt-1">
                            Information about the level to place.
                        </p>

                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.AppField name="level.name">
                                {(field) => (
                                    <field.TextField
                                        label="Name"
                                        required
                                        placeholder="Level name"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="level.description">
                                {(field) => (
                                    <field.TextAreaField
                                        classname="w-lg"
                                        label="Description"
                                        placeholder="Level description"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField
                                name="level.status"
                                listeners={{
                                    onChange: ({ value }) => {
                                        if (
                                            value == 'Pending' ||
                                            value == 'Removed'
                                        )
                                            form.setFieldValue(
                                                'level.position',
                                                null,
                                            );
                                    },
                                }}
                            >
                                {(field) => (
                                    <field.DropdownField
                                        classname="w-fit"
                                        label="Status"
                                        options={levelStatuses}
                                        required
                                        labelNotes="Levels set as Pending do not have a given position yet. MainList/Legacy ones must have one."
                                        placeholder="Select status"
                                    />
                                )}
                            </form.AppField>
                            <form.Subscribe>
                                {({
                                    values: {
                                        level: { status },
                                    },
                                }) => {
                                    if (
                                        status == 'MainList' ||
                                        status == 'Legacy'
                                    )
                                        return (
                                            <form.AppField name="level.position">
                                                {(field) => (
                                                    <field.NumberField
                                                        label="Position"
                                                        required
                                                        placeholder="100"
                                                    />
                                                )}
                                            </form.AppField>
                                        );
                                }}
                            </form.Subscribe>

                            <form.Subscribe>
                                {({ values }) => {
                                    const { name, position, status } =
                                        values.level;
                                    if (
                                        !name ||
                                        !position ||
                                        (status != 'MainList' &&
                                            status != 'Legacy')
                                    )
                                        return null;

                                    const [above, below] = [
                                        levels[position - 2] ?? null,
                                        levels[position - 1] ?? null,
                                    ];

                                    return (
                                        <>
                                            <h3 className="font-semibold w-fit mx-auto mb-2">
                                                Placement preview:
                                            </h3>
                                            <ListEditPreview
                                                above={above}
                                                middle={{
                                                    name,
                                                    position,
                                                    status,
                                                }}
                                                below={{
                                                    ...below,
                                                    position: position + 1,
                                                }}
                                            />
                                        </>
                                    );
                                }}
                            </form.Subscribe>
                            <form.Subscribe>
                                {({
                                    values: {
                                        level: { status },
                                    },
                                }) =>
                                    status == 'Pending' && (
                                        <form.AppField name="level.requires_raw_footage">
                                            {(field) => (
                                                <field.CheckboxField
                                                    label="Requires Raw Footage"
                                                    labelNotes="For Pending levels, whether or not submitting a record should require raw footage"
                                                />
                                            )}
                                        </form.AppField>
                                    )
                                }
                            </form.Subscribe>
                            <form.AppField name="level.level_id">
                                {(field) => (
                                    <field.NumberField
                                        label="Level ID"
                                        required
                                        placeholder="12345678"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="level.publisher">
                                {(field) => (
                                    <field.UserField
                                        label="Publisher"
                                        required
                                        tagOptions={{ showFullName: true }}
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="creators">
                                {(field) => (
                                    <field.UsersField
                                        tagOptions={{
                                            highContrast: true,
                                            showFullName: true,
                                        }}
                                        label="Creators"
                                        required
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="level.tags">
                                {(field) => (
                                    <field.LevelTagsField label="Tags" />
                                )}
                            </form.AppField>
                            <form.AppField name="level.song">
                                {(field) => (
                                    <field.NumberField
                                        label="Song ID"
                                        placeholder="Newgrounds ID"
                                        labelNotes="Leave blank for NONG"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="level.two_player">
                                {(field) => (
                                    <field.CheckboxField label="Two Player" />
                                )}
                            </form.AppField>
                        </div>
                        <h3 className="text-xl font-semibold ml-4 mt-4">
                            Verification
                        </h3>
                        <p className="text-sm text-secondary-light ml-4 mt-1">
                            Information about the verification for the level.
                            This will create an accepted submission for it.
                        </p>
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.AppField name="verification.submitted_by">
                                {(field) => (
                                    <field.UserField
                                        label="Verifier"
                                        tagOptions={{ showFullName: true }}
                                        required
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="verification.video_url">
                                {(field) => (
                                    <field.URLField
                                        label="Verification Video"
                                        placeholder="Video link to the verification"
                                        required
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="verification.mobile">
                                {(field) => (
                                    <field.DropdownBooleanField
                                        placeholder="Device used for the verification"
                                        optionDisabled="PC"
                                        optionEnabled="Mobile"
                                        label="Device"
                                        required
                                    />
                                )}
                            </form.AppField>
                            {list == 'platformer' && (
                                <form.AppField name="verification.completion_time">
                                    {(field) => (
                                        <field.CompletionTimeField label="Completion Time" />
                                    )}
                                </form.AppField>
                            )}
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <form.AppForm>
                                <form.SubscribeButton
                                    label="Add level"
                                    classname="w-11/12"
                                />
                            </form.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};
